import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '@byscott-io/corebyscott';
import { useParams, Link } from 'react-router-dom';
import { apps, products, subscriptions, features, quota, settings, coupons, invoices } from '../api/client';
import { CurrentSubscriptionCard } from '../../../gems/billingbyscott_client/app/javascript/billingbyscott_client/components/CurrentSubscriptionCard';

export default function ClientAccountDetailPage() {
  const { appId, clientId } = useParams();
  const [app, setApp] = useState(null);
  const [clientAccount, setClientAccount] = useState(null);
  const [clientSubscriptions, setClientSubscriptions] = useState([]);
  const [clientInvoices, setClientInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [appResponse, clientResponse, subsResponse, invoicesResponse] = await Promise.all([
        apps.get(appId),
        apps.clientAccount(appId, clientId),
        subscriptions.listForClient(appId, clientId),
        invoices.listForClient(appId, clientId),
      ]);
      setApp(appResponse.data.app);
      setClientAccount(clientResponse.data.client_account);
      setClientSubscriptions(subsResponse.data);
      setClientInvoices(invoicesResponse.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load client account');
    } finally {
      setLoading(false);
    }
  }, [appId, clientId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCancel = async (subscription, immediate = false) => {
    const message = immediate
      ? 'Cancel this subscription immediately?'
      : 'Cancel this subscription at the end of the billing period?';
    if (!confirm(message)) return;

    try {
      await subscriptions.cancel(appId, clientId, subscription.id, immediate);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to cancel subscription');
    }
  };

  const handleReactivate = async (subscription) => {
    if (!confirm('Reactivate this subscription?')) return;

    try {
      await subscriptions.reactivate(appId, clientId, subscription.id);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reactivate subscription');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-gray-500">Loading client account...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <div className="mb-6 text-sm">
        <Link to="/app/apps" className="text-blue-600 hover:text-blue-800">
          Apps
        </Link>
        <span className="mx-2 text-gray-400">/</span>
        <Link to={`/app/apps/${appId}`} className="text-blue-600 hover:text-blue-800">
          {app?.name}
        </Link>
        <span className="mx-2 text-gray-400">/</span>
        <Link to={`/app/apps/${appId}/clients`} className="text-blue-600 hover:text-blue-800">
          Client Accounts
        </Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-900">{clientAccount?.name || clientAccount?.external_id}</span>
      </div>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{clientAccount.name || 'Unnamed Account'}</h1>
        <p className="text-gray-500 font-mono text-sm">{clientAccount.external_id}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Account Info */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Account Info</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">External ID</label>
              <p className="mt-1 text-gray-900 font-mono">{clientAccount.external_id}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Name</label>
              <p className="mt-1 text-gray-900">{clientAccount.name || '—'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Email</label>
              <p className="mt-1 text-gray-900">{clientAccount.email || '—'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Square Customer ID</label>
              <p className="mt-1 text-gray-900 font-mono text-sm">
                {clientAccount.square_customer_id || '—'}
              </p>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Details</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Created</label>
              <p className="mt-1 text-gray-900">
                {new Date(clientAccount.created_at).toLocaleString()}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Last Updated</label>
              <p className="mt-1 text-gray-900">
                {new Date(clientAccount.updated_at).toLocaleString()}
              </p>
            </div>

            {clientAccount.metadata && Object.keys(clientAccount.metadata).length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-500">Metadata</label>
                <pre className="mt-1 text-sm bg-gray-100 rounded p-3 overflow-x-auto">
                  {JSON.stringify(clientAccount.metadata, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subscriptions */}
      <div className="mt-6 bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Subscriptions</h2>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm"
          >
            + New Subscription
          </button>
        </div>

        {clientSubscriptions.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500">
            <p>No subscriptions yet</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="text-blue-600 hover:text-blue-800 text-sm mt-2"
            >
              Create a subscription
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {clientSubscriptions.map((sub) => (
              <SubscriptionCard
                key={sub.id}
                subscription={sub}
                onManage={() => setSelectedSubscription(sub)}
                onCancel={() => handleCancel(sub, false)}
                onReactivate={() => handleReactivate(sub)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Invoices */}
      <div className="mt-6 bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">Invoices</h2>
        {clientInvoices.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500">
            <p>No invoices yet</p>
            <p className="text-sm mt-1">Invoices will appear here once generated.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {clientInvoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {new Date(invoice.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {invoice.subscription.product} - {invoice.subscription.level}
                    </td>
                    <td className="px-4 py-3">
                      <InvoiceStatusBadge status={invoice.status} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      ${(invoice.total_cents / 100).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <Link
                        to={`/app/apps/${appId}/clients/${clientId}/invoices/${invoice.id}`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateSubscriptionModal
          appId={appId}
          clientId={clientId}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            loadData();
          }}
        />
      )}

      {selectedSubscription && (
        <SubscriptionDetailModal
          appId={appId}
          clientId={clientId}
          subscription={selectedSubscription}
          onClose={() => setSelectedSubscription(null)}
          onUpdated={() => {
            setSelectedSubscription(null);
            loadData();
          }}
        />
      )}
    </div>
  );
}

function SubscriptionCard({ subscription, onManage, onCancel, onReactivate }) {
  const sub = subscription;
  const items = sub.items || [];

  return (
    <div className="relative">
      {/* Admin Header - status badges and actions */}
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <StatusBadge status={sub.status} canceledAt={sub.canceled_at} />
          {sub.billing_exempt && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800" title={sub.billing_exempt_reason || 'Billing exempt'}>
              Exempt
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={onManage}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Manage
          </button>
          {(sub.status === 'trialing' || sub.status === 'active') && !sub.canceled_at && (
            <button
              onClick={onCancel}
              className="text-red-600 hover:text-red-800 text-sm"
            >
              Cancel
            </button>
          )}
          {sub.canceled_at && sub.status !== 'canceled' && (
            <button
              onClick={onReactivate}
              className="text-green-600 hover:text-green-800 text-sm"
            >
              Reactivate
            </button>
          )}
        </div>
      </div>

      {/* Shared subscription display component */}
      <CurrentSubscriptionCard
        subscription={sub}
        items={items}
        products={[]}
        showActions={false}
      />
    </div>
  );
}

function StatusBadge({ status, canceledAt }) {
  const colors = {
    trialing: 'bg-yellow-100 text-yellow-800',
    active: 'bg-green-100 text-green-800',
    past_due: 'bg-red-100 text-red-800',
    canceled: 'bg-gray-100 text-gray-800',
  };

  let displayStatus = status;
  if (canceledAt && status !== 'canceled') {
    displayStatus = 'canceling';
  }

  return (
    <span className={`px-2 py-0.5 text-xs rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {displayStatus}
    </span>
  );
}

function InvoiceStatusBadge({ status }) {
  const colors = {
    draft: 'bg-gray-100 text-gray-800',
    open: 'bg-yellow-100 text-yellow-800',
    paid: 'bg-green-100 text-green-800',
    void: 'bg-gray-100 text-gray-600',
    uncollectible: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`px-2 py-0.5 text-xs rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  );
}

function CreateSubscriptionModal({ appId, clientId, onClose, onCreated }) {
  const [productsList, setProductsList] = useState([]);
  const [couponsList, setCouponsList] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [licenseCount, setLicenseCount] = useState(1);
  const [selectedCoupon, setSelectedCoupon] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const [productsRes, couponsRes] = await Promise.all([
          products.list(appId),
          coupons.list(appId),
        ]);
        setProductsList(productsRes.data);
        setCouponsList(couponsRes.data.filter(c => c.active));
      } catch {
        setError('Failed to load products');
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, [appId]);

  const selectedProductData = productsList.find(p => p.id === parseInt(selectedProduct));
  const selectedLevelData = selectedProductData?.levels?.find(l => l.id === parseInt(selectedLevel));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLevel) return;

    try {
      setSaving(true);
      setError(null);
      await subscriptions.create(appId, clientId, {
        product_level_id: selectedLevel,
        license_count: licenseCount,
        coupon_id: selectedCoupon || undefined,
      });
      onCreated();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.join(', ') || 'Failed to create subscription');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="New Subscription" size="md">
      {loading ? (
          <div className="text-gray-500 py-4">Loading products...</div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
              <select
                value={selectedProduct}
                onChange={(e) => {
                  setSelectedProduct(e.target.value);
                  setSelectedLevel('');
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
              >
                <option value="">Select a product...</option>
                {productsList.filter(p => p.active).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            {selectedProduct && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
                <select
                  value={selectedLevel}
                  onChange={(e) => setSelectedLevel(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                >
                  <option value="">Select a level...</option>
                  {selectedProductData?.levels?.filter(l => l.active).map((l) => {
                    const monthlyPrice = l.prices?.find(p => p.billing_interval === 'month');
                    const price = monthlyPrice?.price_cents || 0;
                    return (
                      <option key={l.id} value={l.id}>
                        {l.name} - ${(price / 100).toFixed(2)}/mo ({l.billing_type})
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            {selectedLevelData && selectedLevelData.billing_type === 'per_user' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  License Count
                  {selectedLevelData.included_users > 0 && (
                    <span className="text-gray-500 font-normal"> (includes {selectedLevelData.included_users})</span>
                  )}
                </label>
                <input
                  type="number"
                  min={selectedLevelData.included_users || 1}
                  value={licenseCount}
                  onChange={(e) => setLicenseCount(parseInt(e.target.value) || 1)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            )}

            {couponsList.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Coupon (optional)</label>
                <select
                  value={selectedCoupon}
                  onChange={(e) => setSelectedCoupon(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">No coupon</option>
                  {couponsList.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} - {c.discount_type === 'percent' ? `${c.discount_value}%` : `$${(c.discount_value / 100).toFixed(2)}`} off
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedLevelData && (() => {
              const monthlyPrice = selectedLevelData.prices?.find(p => p.billing_interval === 'month');
              const basePriceCents = monthlyPrice?.price_cents || 0;
              const perUserPriceCents = monthlyPrice?.price_per_user_cents || 0;
              return (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Base price:</span>
                      <span>${(basePriceCents / 100).toFixed(2)}/mo</span>
                    </div>
                    {selectedLevelData.billing_type === 'per_user' && licenseCount > (selectedLevelData.included_users || 0) && (
                      <div className="flex justify-between mt-1">
                        <span>Additional users ({licenseCount - (selectedLevelData.included_users || 0)}):</span>
                        <span>${(((licenseCount - (selectedLevelData.included_users || 0)) * perUserPriceCents) / 100).toFixed(2)}/mo</span>
                      </div>
                    )}
                    {selectedLevelData.trial_days > 0 && (
                      <div className="mt-2 text-green-600">
                        {selectedLevelData.trial_days}-day free trial
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !selectedLevel}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Creating...' : 'Create Subscription'}
              </button>
            </div>
          </form>
        )}
    </Modal>
  );
}

function SubscriptionDetailModal({ appId, clientId, subscription, onClose, onUpdated }) {
  const [subDetail, setSubDetail] = useState(null);
  const [featuresList, setFeaturesList] = useState([]);
  const [quotaList, setQuotaList] = useState([]);
  const [settingsList, setSettingsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    const loadDetails = async () => {
      try {
        const [subRes, featuresRes, quotaRes, settingsRes] = await Promise.all([
          subscriptions.get(appId, clientId, subscription.id),
          features.list(appId),
          quota.list(appId),
          settings.list(appId),
        ]);
        setSubDetail(subRes.data);
        setFeaturesList(featuresRes.data);
        setQuotaList(quotaRes.data);
        setSettingsList(settingsRes.data);
      } catch (err) {
        console.error('Failed to load subscription details', err);
      } finally {
        setLoading(false);
      }
    };
    loadDetails();
  }, [appId, clientId, subscription.id]);

  const handleAddOverride = async (type, id, value) => {
    try {
      const override = { type };
      if (type === 'feature') {
        override.feature_id = id;
        override.enabled = value;
      } else if (type === 'quota') {
        override.quotum_id = id;
        override.value = value;
      } else if (type === 'setting') {
        override.setting_id = id;
        override.value = value;
      }
      await subscriptions.addOverride(appId, clientId, subscription.id, override);
      onUpdated();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add override');
    }
  };

  const handleRemoveOverride = async (type, id) => {
    const override = { type };
    if (type === 'feature') override.feature_id = id;
    else if (type === 'quota') override.quotum_id = id;
    else if (type === 'setting') override.setting_id = id;

    try {
      await subscriptions.removeOverride(appId, clientId, subscription.id, override);
      onUpdated();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove override');
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={subscription.product.name}
      size="xl"
      footer={
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
        >
          Close
        </button>
      }
    >
      <p className="text-gray-500">{subscription.level.name}</p>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="-mb-px flex space-x-8">
            {['details', 'billing', 'overrides', 'users'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        {loading ? (
          <div className="text-gray-500 py-4">Loading...</div>
        ) : (
          <>
            {activeTab === 'details' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Status</label>
                    <p><StatusBadge status={subDetail.status} canceledAt={subDetail.canceled_at} /></p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Billing Type</label>
                    <p className="font-mono text-sm">{subDetail.level.billing_type}</p>
                  </div>
                  {!subDetail.billing_exempt && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Price</label>
                      <p>${(subDetail.current_price_cents / 100).toFixed(2)}/mo</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-500">Licenses</label>
                    <p>{subDetail.assigned_users_count}/{subDetail.license_count || '∞'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Period Start</label>
                    <p>{subDetail.current_period_start ? new Date(subDetail.current_period_start).toLocaleDateString() : '—'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Period End</label>
                    <p>{subDetail.current_period_end ? new Date(subDetail.current_period_end).toLocaleDateString() : '—'}</p>
                  </div>
                  {subDetail.trial_ends_at && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Trial Ends</label>
                      <p>{new Date(subDetail.trial_ends_at).toLocaleDateString()}</p>
                    </div>
                  )}
                  {subDetail.canceled_at && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Canceled At</label>
                      <p className="text-red-600">{new Date(subDetail.canceled_at).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
                {subDetail.coupon && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg">
                    <span className="text-sm font-medium text-green-800">
                      Coupon: {subDetail.coupon.code} ({subDetail.coupon.discount_type === 'percent' ? `${subDetail.coupon.discount_value}%` : `$${(subDetail.coupon.discount_value / 100).toFixed(2)}`} off)
                    </span>
                  </div>
                )}

                {/* Trial Management */}
                {subDetail.status === 'trialing' && (
                  <TrialManagement
                    appId={appId}
                    clientId={clientId}
                    subscription={subDetail}
                    onUpdated={onUpdated}
                  />
                )}
              </div>
            )}

            {activeTab === 'billing' && (
              <BillingExemptSection
                appId={appId}
                clientId={clientId}
                subscription={subDetail}
                onUpdated={onUpdated}
              />
            )}

            {activeTab === 'overrides' && (
              <div className="space-y-6">
                {/* Feature Overrides */}
                <div>
                  <h3 className="font-medium mb-2">Feature Overrides</h3>
                  <div className="space-y-2">
                    {featuresList.map((feature) => {
                      const override = subDetail.overrides?.features?.find(o => o.feature_id === feature.id);
                      return (
                        <div key={feature.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm">{feature.name} <span className="text-gray-500">({feature.key})</span></span>
                          <div className="flex items-center gap-2">
                            {override ? (
                              <>
                                <span className={`text-sm ${override.enabled ? 'text-green-600' : 'text-red-600'}`}>
                                  {override.enabled ? 'Enabled' : 'Disabled'}
                                </span>
                                <button
                                  onClick={() => handleRemoveOverride('feature', feature.id)}
                                  className="text-red-600 hover:text-red-800 text-xs"
                                >
                                  Remove
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleAddOverride('feature', feature.id, true)}
                                  className="text-green-600 hover:text-green-800 text-xs"
                                >
                                  Enable
                                </button>
                                <button
                                  onClick={() => handleAddOverride('feature', feature.id, false)}
                                  className="text-red-600 hover:text-red-800 text-xs"
                                >
                                  Disable
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {featuresList.length === 0 && (
                      <p className="text-gray-500 text-sm">No features defined for this app.</p>
                    )}
                  </div>
                </div>

                {/* Quota Overrides */}
                <div>
                  <h3 className="font-medium mb-2">Quota Overrides</h3>
                  <div className="space-y-2">
                    {quotaList.map((q) => {
                      const override = subDetail.overrides?.quota?.find(o => o.quotum_id === q.id);
                      return (
                        <div key={q.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm">{q.name} <span className="text-gray-500">({q.key})</span></span>
                          <div className="flex items-center gap-2">
                            {override ? (
                              <>
                                <span className="text-sm font-mono">{override.value}</span>
                                <button
                                  onClick={() => handleRemoveOverride('quota', q.id)}
                                  className="text-red-600 hover:text-red-800 text-xs"
                                >
                                  Remove
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => {
                                  const value = prompt(`Enter quota value for ${q.name}:`);
                                  if (value !== null) handleAddOverride('quota', q.id, parseInt(value));
                                }}
                                className="text-blue-600 hover:text-blue-800 text-xs"
                              >
                                Set Override
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {quotaList.length === 0 && (
                      <p className="text-gray-500 text-sm">No quota defined for this app.</p>
                    )}
                  </div>
                </div>

                {/* Setting Overrides */}
                <div>
                  <h3 className="font-medium mb-2">Setting Overrides</h3>
                  <div className="space-y-2">
                    {settingsList.map((s) => {
                      const override = subDetail.overrides?.settings?.find(o => o.setting_id === s.id);
                      return (
                        <div key={s.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm">{s.name} <span className="text-gray-500">({s.key})</span></span>
                          <div className="flex items-center gap-2">
                            {override ? (
                              <>
                                <span className="text-sm font-mono truncate max-w-[150px]">{JSON.stringify(override.value)}</span>
                                <button
                                  onClick={() => handleRemoveOverride('setting', s.id)}
                                  className="text-red-600 hover:text-red-800 text-xs"
                                >
                                  Remove
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => {
                                  const value = prompt(`Enter setting value (JSON) for ${s.name}:`);
                                  if (value !== null) {
                                    try {
                                      handleAddOverride('setting', s.id, JSON.parse(value));
                                    } catch {
                                      handleAddOverride('setting', s.id, value);
                                    }
                                  }
                                }}
                                className="text-blue-600 hover:text-blue-800 text-xs"
                              >
                                Set Override
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {settingsList.length === 0 && (
                      <p className="text-gray-500 text-sm">No settings defined for this app.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div>
                {subDetail.items?.length > 0 ? (
                  <div className="space-y-6">
                    {subDetail.items.map((item) => (
                      <div key={item.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-medium">{item.level?.name || item.product?.name || 'Plan'}</h4>
                          <span className="text-sm text-gray-600">
                            {item.assigned_users?.length || 0} of {item.license_count || '∞'} licenses used
                          </span>
                        </div>
                        {item.level?.billing_type === 'flat' ? (
                          <p className="text-gray-500 text-sm">Flat-rate plan - no user assignment required.</p>
                        ) : item.assigned_users?.length > 0 ? (
                          <div className="space-y-2">
                            {item.assigned_users.map((user) => (
                              <div key={user.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="font-mono text-sm">{user.external_user_id}</span>
                                <span className="text-xs text-gray-500">
                                  Added {new Date(user.assigned_at).toLocaleDateString()}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">No users assigned yet.</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : subDetail.level?.billing_type === 'flat' ? (
                  <p className="text-gray-500">Flat-rate subscriptions do not require user assignment.</p>
                ) : (
                  <p className="text-gray-500 text-sm">No subscription items found.</p>
                )}
              </div>
            )}
          </>
        )}
    </Modal>
  );
}

function BillingExemptSection({ appId, clientId, subscription, onUpdated }) {
  const [billingExempt, setBillingExempt] = useState(subscription.billing_exempt || false);
  const [exemptReason, setExemptReason] = useState(subscription.billing_exempt_reason || '');
  const [internalNotes, setInternalNotes] = useState(subscription.internal_notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      await subscriptions.update(appId, clientId, subscription.id, {
        billing_exempt: billingExempt,
        billing_exempt_reason: billingExempt ? exemptReason : null,
        internal_notes: internalNotes || null,
      });
      onUpdated();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update billing status');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = billingExempt !== (subscription.billing_exempt || false) ||
    exemptReason !== (subscription.billing_exempt_reason || '') ||
    internalNotes !== (subscription.internal_notes || '');

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-medium mb-3">Billing Exempt Status</h3>
        <p className="text-sm text-gray-600 mb-4">
          Billing exempt subscriptions will not be charged during the billing cycle.
          Use this for non-profits, internal accounts, beta testers, partners, or other special cases.
        </p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="billingExempt"
            checked={billingExempt}
            onChange={(e) => setBillingExempt(e.target.checked)}
            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          />
          <label htmlFor="billingExempt" className="text-sm font-medium text-gray-700">
            Mark as billing exempt (non-paying)
          </label>
        </div>

        {billingExempt && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason for exemption (visible to account admin)
            </label>
            <textarea
              value={exemptReason}
              onChange={(e) => setExemptReason(e.target.value)}
              placeholder="e.g., Non-profit organization, Beta tester, Partner account..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              rows={2}
            />
            <p className="text-xs text-amber-600 mt-1">
              This reason will be visible to the account admin on their billing page.
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Internal notes (not visible to account admin)
          </label>
          <textarea
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            placeholder="Internal notes about this subscription, billing arrangements, contacts, etc..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            rows={3}
          />
          <p className="text-xs text-gray-500 mt-1">
            These notes are only visible to BillingByScott admins.
          </p>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {subscription.billing_exempt && (
        <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-start gap-2">
            <span className="text-purple-600 text-lg">🎁</span>
            <div>
              <p className="text-sm font-medium text-purple-900">This subscription is billing exempt</p>
              {subscription.billing_exempt_reason && (
                <p className="text-sm text-purple-700 mt-1">{subscription.billing_exempt_reason}</p>
              )}
              <p className="text-xs text-purple-600 mt-2">
                This subscription will not be charged during automated billing cycles.
              </p>
            </div>
          </div>
        </div>
      )}

      {subscription.internal_notes && (
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-xs font-medium text-gray-500 mb-1">Internal Notes</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{subscription.internal_notes}</p>
        </div>
      )}
    </div>
  );
}

function TrialManagement({ appId, clientId, subscription, onUpdated }) {
  const [extendDays, setExtendDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const trialEndsAt = subscription.trial_ends_at ? new Date(subscription.trial_ends_at) : null;
  const daysRemaining = trialEndsAt
    ? Math.max(0, Math.ceil((trialEndsAt - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  const handleExtendTrial = async () => {
    if (!confirm(`Extend trial by ${extendDays} days?`)) return;

    setLoading(true);
    setError(null);

    try {
      await subscriptions.extendTrial(appId, clientId, subscription.id, extendDays);
      onUpdated();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to extend trial');
    } finally {
      setLoading(false);
    }
  };

  const handleConvertTrial = async (chargeImmediately) => {
    const message = chargeImmediately
      ? 'Convert trial to paid and charge immediately?'
      : 'Convert trial to paid without charging (first invoice will be at period end)?';

    if (!confirm(message)) return;

    setLoading(true);
    setError(null);

    try {
      await subscriptions.convertTrial(appId, clientId, subscription.id, chargeImmediately);
      onUpdated();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to convert trial');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
      <h4 className="font-medium text-orange-900 mb-3">Trial Management</h4>

      {error && (
        <div className="mb-3 p-2 bg-red-100 text-red-700 rounded text-sm">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {/* Trial Status */}
        <div className="flex items-center gap-4 text-sm">
          <span className="text-orange-800">
            <strong>{daysRemaining}</strong> days remaining
          </span>
          {trialEndsAt && (
            <span className="text-gray-600">
              Ends: {trialEndsAt.toLocaleDateString()}
            </span>
          )}
        </div>

        {/* Extend Trial */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-700">Extend by:</label>
          <input
            type="number"
            min="1"
            max="365"
            value={extendDays}
            onChange={(e) => setExtendDays(parseInt(e.target.value) || 7)}
            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
          />
          <span className="text-sm text-gray-600">days</span>
          <button
            onClick={handleExtendTrial}
            disabled={loading}
            className="px-3 py-1 bg-orange-600 text-white rounded text-sm hover:bg-orange-700 disabled:opacity-50"
          >
            Extend Trial
          </button>
        </div>

        {/* Convert Trial */}
        <div className="flex items-center gap-2 pt-2 border-t border-orange-200">
          <span className="text-sm text-gray-700">Convert to paid:</span>
          <button
            onClick={() => handleConvertTrial(true)}
            disabled={loading}
            className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
          >
            Convert & Charge Now
          </button>
          <button
            onClick={() => handleConvertTrial(false)}
            disabled={loading}
            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            Convert (No Charge)
          </button>
        </div>
      </div>
    </div>
  );
}
